import { isQuotaExceededError } from './StorageManager.ts';

const STATSIG_FAILED_LOGS_PREFIX = 'statsig.failed_logs.';

type PatchState = {
	setItem: Storage['setItem'];
	blockStatsigLocalStorage: boolean;
	onQuotaExceeded?: (key: string, attemptedValueChars?: number) => void;
	reporting: boolean;
};

const patches = new WeakMap<Storage, PatchState>();

/**
 * Monkey patch for local storage to catch "Quota Exceeded" errors
 * See https://jira.atlassian.com/browse/CONFDEV-67240.
 */
export const patchLocalStorage = (
	blockStatsigLocalStorage = false,
	onQuotaExceeded?: (key: string, attemptedValueChars?: number) => void,
): void => {
	const storage = window.localStorage;
	const existing = patches.get(storage);
	if (existing && storage.setItem === existing.setItem) {
		existing.blockStatsigLocalStorage = blockStatsigLocalStorage;
		existing.onQuotaExceeded = onQuotaExceeded;
		return;
	}
	// Storage's named-property setter can turn an instance assignment into a stored string.
	// Patch the native prototype, and restrict interception to this document's localStorage.
	const target = storage instanceof Storage ? Storage.prototype : storage;
	const realSetItem = target.setItem;
	const state: PatchState = {
		setItem: newSetItem,
		blockStatsigLocalStorage,
		onQuotaExceeded,
		reporting: false,
	};

	function newSetItem(this: Storage, ...args: [string, string]): void {
		if (this !== storage) return realSetItem.apply(this, args);
		try {
			const [key] = args;

			// Block statsig.failed_logs.* to prevent unbounded localStorage growth
			// Note this is a temporary fix that will be removed when a fix is deployed in statsig: https://product-fabric.atlassian.net/browse/CCP-7527
			if (state.blockStatsigLocalStorage && key.startsWith(STATSIG_FAILED_LOGS_PREFIX)) {
				return;
			}

			return realSetItem.apply(this, args);
		} catch (e) {
			if (isQuotaExceededError(e)) {
				if (!state.reporting && state.onQuotaExceeded) {
					state.reporting = true;
					try {
						state.onQuotaExceeded(
							args[0],
							typeof args[1] === 'string' ? args[1].length : undefined,
						);
					} catch {
						// Reporting must not replace the original storage error or prevent the warning.
					} finally {
						state.reporting = false;
					}
				}
				window.dispatchEvent(new CustomEvent('local-storage.quota.exceeded'));
			}
			throw e;
		}
	}

	target.setItem = newSetItem;
	patches.set(storage, state);
};
